import { ReactNode, useEffect, useRef } from "react";

import { useBreakpointValue } from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";

import { Grid, GridItem, Box } from "@/storybook-components";

interface GridContentProps {
  leftPanel: ReactNode;
  contentBody: ReactNode;
  sidebarState: string;
  setCurrentContext: any;
}

const MotionGrid = motion(Grid);
const MotionGridItem = motion(GridItem);

const GridContent = ({
  leftPanel,
  contentBody,
  sidebarState,
  setCurrentContext
}: GridContentProps): JSX.Element => {
  const panelVariants = {
    expand: {
      width: "100%",
      position: "relative",
      transition: { duration: 0.2 },
      transitionEnd: { display: "block" }
    },
    collapse: {
      width: ["33%", "0%"],
      position: "absolute",
      height: "100vh",
      transition: { duration: 0.5 },
      transitionEnd: { display: "none" }
    }
  };

  const GridVariants = useBreakpointValue({
    base: sidebarState === "expand" ? true : false,
    lg: false
  });

  const currentRef = useRef(null);
  useEffect(() => {
    setCurrentContext(currentRef?.current);
  }, [setCurrentContext, sidebarState]);

  return (
    <AnimatePresence initial={false}>
      <MotionGrid
        gridTemplateColumns={{
          base: "repeat(4, 1fr)",
          lg: "repeat(12, 1fr)"
        }}
        sx={{
          ".ehq-ideas-about-grid": {
            position: "sticky !important",
            top: "0 !important",
            height: { base: "auto !important", md: "100vh !important" },
            overflowY: "visible !important",
            display: `${
              sidebarState === "expand" ? "block !important" : "none !important"
            }`
          },
          ".ehq-ideas-about": {
            pt: 0,
            gap: 0
          },
          ".ehq-ideas-about-header": {
            background: "gray.100",
            position: "sticky",
            overflow: "hidden",
            top: 0,
            py: 6,
            zIndex: 2
          },
          ".ehq-ideas-about-content": {
            pb: 0,
            minHeight: "auto !important"
          },
          ".ehq-ideas-about-footer": {
            height: "100% !important"
          },
          "#ideas-grid": {
            display: { base: "flex", lg: "block" },
            minHeight: "100vh",
            minWidth: "21em",
            flexDirection: "column",
            pb: { base: 2, md: 6 }
          },
          ".ehq-ideas-list": {
            width: { base: "100%", lg: "auto" }
          },
          ".ehq-sticky-action": {
            mt: "auto",
            width: { base: "100%", lg: "auto" },
            position: "sticky !important",
            order: { base: 2, lg: "initial" },
            zIndex: 20,
            top: 0,
            px: 0
          },
          ".ehq-sticky-action .chakra-popover__popper ": {
            margin: 0
          }
        }}
      >
        {/*
         * Grid for left Sliding Panel
         **/}
        <MotionGridItem
          rowSpan={1}
          animate={sidebarState}
          variants={panelVariants}
          colSpan={{
            base: 4,
            lg: sidebarState === "expand" ? 6 : 0,
            xl: sidebarState === "expand" ? 4 : 0,
            "2xl": sidebarState === "expand" ? 3 : 0
          }}
          overflow="hidden"
          className="ehq-ideas-about-grid"
        >
          <Box
            h="full"
            ref={currentRef}
            hidden={sidebarState === "expand" ? false : true}
          >
            {leftPanel}
          </Box>
        </MotionGridItem>
        {/*
         * Grid for Main content Panel
         **/}
        <MotionGridItem
          rowSpan={1}
          colSpan={{
            base: 12,
            lg: sidebarState === "expand" ? 6 : 12,
            xl: sidebarState === "expand" ? 8 : 12,
            "2xl": sidebarState === "expand" ? 9 : 12
          }}
          hidden={GridVariants}
          overflow="visible"
        >
          {contentBody}
        </MotionGridItem>
      </MotionGrid>
    </AnimatePresence>
  );
};

export default GridContent;
